
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import tradingCostingService from "../services/tradingCostingService";

const styles = {
  page: {
    padding: "24px",
    background: "#f7f8fc",
    minHeight: "100%",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#252b3b",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "22px",
  },
  title: {
    margin: 0,
    fontSize: "25px",
    fontWeight: 700,
  },
  subtitle: {
    margin: "7px 0 0",
    color: "#747b8d",
    fontSize: "13px",
  },
  button: {
    border: 0,
    borderRadius: "8px",
    padding: "11px 17px",
    background: "#6254d9",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  card: {
    background: "#fff",
    border: "1px solid #e8eaf2",
    borderRadius: "12px",
    padding: "18px",
    marginBottom: "18px",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
    marginBottom: "20px",
  },
  statLabel: {
    color: "#747b8d",
    fontSize: "12px",
    marginBottom: "8px",
  },
  statValue: {
    fontSize: "24px",
    fontWeight: 700,
  },
  filters: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  input: {
    minWidth: "220px",
    flex: 1,
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "11px 12px",
    fontSize: "13px",
    outline: "none",
  },
  select: {
    minWidth: "170px",
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "10px 12px",
    background: "#fff",
    fontSize: "13px",
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1000px",
  },
  th: {
    textAlign: "left",
    padding: "13px 12px",
    background: "#f7f8fc",
    color: "#70778a",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: ".4px",
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "14px 12px",
    fontSize: "13px",
    borderBottom: "1px solid #f0f1f6",
    whiteSpace: "nowrap",
  },
  link: {
    color: "#5548cb",
    fontWeight: 700,
    cursor: "pointer",
    border: 0,
    padding: 0,
    background: "transparent",
  },
  badge: {
    display: "inline-block",
    borderRadius: "20px",
    padding: "5px 9px",
    fontSize: "11px",
    fontWeight: 700,
    background: "#efedff",
    color: "#5548cb",
  },
  error: {
    padding: "12px 14px",
    background: "#fff0f0",
    color: "#b42318",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  muted: {
    color: "#858b9b",
    fontSize: "13px",
    textAlign: "center",
    padding: "32px 12px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "15px",
    color: "#747b8d",
    fontSize: "12px",
  },
};

const getRows = (response) => {
  const body = response?.data?.data ?? response?.data;

  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.items)) return body.items;
  if (Array.isArray(body?.costings)) return body.costings;

  return [];
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatAmount = (value, currency = "INR") => {
  const amount = Number(value ?? 0);

  if (!Number.isFinite(amount)) return "—";

  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
};

const normalizeStatus = (value) =>
  String(value || "DRAFT")
    .trim()
    .toUpperCase();

const getEnquiryNumber = (row) =>
  row.enquiry_no ||
  row.enquiry_number ||
  row.enquiry?.enquiry_no ||
  row.enquiry?.enquiry_number ||
  "—";

const getStatusClass = (status) =>
  String(status || "DRAFT")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

export default function TradingCostingListPage() {
  const navigate = useNavigate();

  const [costings, setCostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(1);

  const pageSize = 10;

  const loadCostings = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await tradingCostingService.getCostings();
      setCostings(getRows(response));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to load Trading Costings."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCostings();
  }, [loadCostings]);

  const statuses = [
    ...new Set(costings.map((row) => normalizeStatus(row.status_code))),
  ].sort();

  const filtered = costings.filter((row) => {
    const status = normalizeStatus(row.status_code);
    const term = search.trim().toLowerCase();

    const searchableValues = [
      row.costing_no,
      getEnquiryNumber(row),
      row.enquiry_id,
      row.remarks,
    ];

    const matchesSearch =
      !term ||
      searchableValues.some((value) =>
        String(value ?? "").toLowerCase().includes(term)
      );

    const matchesStatus =
      statusFilter === "ALL" || status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const visibleRows = filtered.slice(startIndex, startIndex + pageSize);

  const totalCostings = costings.length;

  const currentCostings = costings.filter(
    (row) => row.is_current_version !== false
  ).length;

  const approvedCostings = costings.filter(
    (row) => normalizeStatus(row.status_code) === "APPROVED"
  ).length;

  const openCosting = (row) => {
    if (row.id) {
      navigate(`/trading/costings/${row.id}`);
    }
  };

  return (
    <div className="trading-costing-page" style={styles.page}>
      <style>{`
        .trading-costing-page {
          background: #f7f9fc !important;
          color: #172033 !important;
          font-family: Inter, Arial, sans-serif;
          box-sizing: border-box;
        }

        .trading-costing-header {
          align-items: center;
          margin-bottom: 22px !important;
        }

        .trading-costing-header h1 {
          color: #172033;
          font-size: 27px !important;
          letter-spacing: -.4px;
        }

        .trading-costing-header p {
          color: #778196 !important;
        }

        .trading-costing-header > button {
          background: linear-gradient(135deg, #1769e8, #0647bd) !important;
          box-shadow: 0 5px 14px rgba(23,105,232,.2);
          border-radius: 9px !important;
          padding: 12px 18px !important;
        }

        .trading-costing-stats {
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          gap: 16px !important;
          margin-bottom: 20px !important;
        }

        .trading-costing-stat-card {
          position: relative;
          overflow: hidden;
          min-height: 98px;
          margin-bottom: 0 !important;
          border: 0 !important;
          border-radius: 14px !important;
          padding: 19px 20px !important;
          box-shadow: 0 7px 18px rgba(30,55,100,.11);
        }

        .trading-costing-stat-card::after {
          content: "";
          position: absolute;
          width: 150px;
          height: 110px;
          right: -35px;
          bottom: -70px;
          border-radius: 50%;
          background: rgba(255,255,255,.13);
          pointer-events: none;
        }

        .trading-costing-stat-card:nth-child(1) {
          background: linear-gradient(125deg, #20a4f3, #0860e8 72%, #0747c8) !important;
        }

        .trading-costing-stat-card:nth-child(2) {
          background: linear-gradient(125deg, #a78bfa, #8250ed 58%, #6934d8) !important;
        }

        .trading-costing-stat-card:nth-child(3) {
          background: linear-gradient(125deg, #42d392, #16a66a 58%, #087c4c) !important;
        }

        .trading-costing-stat-card > div:first-child {
          position: relative;
          z-index: 1;
          color: rgba(255,255,255,.96) !important;
          font-weight: 650;
          margin-bottom: 10px !important;
        }

        .trading-costing-stat-card > div:last-child {
          position: relative;
          z-index: 1;
          color: #fff !important;
          font-size: 29px !important;
          line-height: 1.1;
        }

        .trading-costing-list-panel {
          padding: 17px !important;
          margin-bottom: 0 !important;
          border: 1px solid #e6eaf1 !important;
          border-radius: 13px !important;
          box-shadow: 0 3px 12px rgba(21,37,68,.045);
        }

        .trading-costing-filters {
          align-items: center;
          gap: 12px !important;
          margin-bottom: 16px !important;
        }

        .trading-costing-filters input {
          height: 43px;
          min-width: 220px;
          border: 1px solid #cfd6e2 !important;
          border-radius: 8px !important;
          padding: 0 12px !important;
          background: #fff;
          box-sizing: border-box;
        }

        .trading-costing-filters select {
          height: 43px;
          border: 1px solid #cfd6e2 !important;
          border-radius: 8px !important;
        }

        .trading-costing-filters button {
          height: 43px;
          border-radius: 8px !important;
          padding: 0 17px !important;
        }

        .trading-costing-table-wrap {
          border: 1px solid #e6eaf1;
          border-radius: 10px;
          overflow-x: auto;
        }

        .trading-costing-table {
          min-width: 1000px !important;
          border-collapse: separate !important;
          border-spacing: 0 !important;
          font-size: 13px !important;
        }

        .trading-costing-table thead th {
          background: #f1f4fa !important;
          color: #29354a !important;
          font-size: 11px !important;
          font-weight: 750 !important;
          letter-spacing: 0 !important;
          text-transform: none !important;
          padding: 13px 12px !important;
          border-bottom: 1px solid #e2e7f0 !important;
        }

        .trading-costing-table tbody td {
          padding: 13px 12px !important;
          border-bottom: 1px solid #edf0f5 !important;
          color: #29354a !important;
          font-size: 12px !important;
        }

        .trading-costing-table tbody tr:hover td {
          background: #f8faff;
        }

        .trading-costing-table .trading-costing-enquiry-no {
          color: #145ac7 !important;
          font-weight: 700;
        }

        .trading-costing-table button {
          color: #145ac7 !important;
          text-decoration: none;
        }

        .trading-costing-table .trading-costing-status {
          display: inline-flex !important;
          align-items: center;
          border-radius: 30px !important;
          padding: 5px 10px !important;
          font-size: 11px !important;
          font-weight: 750 !important;
        }

        .trading-costing-table .status-approved {
          background: #dcfce7 !important;
          color: #166534 !important;
        }

        .trading-costing-table .status-draft {
          background: #e0e7ff !important;
          color: #3730a3 !important;
        }

        .trading-costing-table .status-pending-approval {
          background: #fef3c7 !important;
          color: #92400e !important;
        }

        .trading-costing-table .status-rejected {
          background: #fee2e2 !important;
          color: #b91c1c !important;
        }

        .trading-costing-footer {
          margin-top: 18px !important;
          color: #667085 !important;
        }

        .trading-costing-footer button {
          background: #fff !important;
          border-color: #dce2ec !important;
          border-radius: 7px !important;
          color: #33415a !important;
          padding: 8px 12px !important;
        }

        @media (max-width: 760px) {
          .trading-costing-page {
            padding: 14px !important;
          }

          .trading-costing-stats {
            grid-template-columns: 1fr !important;
          }

          .trading-costing-filters {
            flex-direction: column;
            align-items: stretch;
          }

          .trading-costing-filters input,
          .trading-costing-filters select {
            width: 100%;
            min-width: 0;
          }
        }
      `}</style>

      <div className="trading-costing-header" style={styles.header}>
        <div>
          <h1 style={styles.title}>Trading Costing</h1>
          <p style={styles.subtitle}>
            Review product costings, margins and proposed selling amounts.
          </p>
        </div>

        <button
          type="button"
          style={styles.button}
          onClick={() => navigate("/trading/enquiries")}
        >
          View Trading Enquiries
        </button>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
          <button
            type="button"
            onClick={loadCostings}
            style={{
              marginLeft: "12px",
              border: 0,
              background: "transparent",
              color: "#b42318",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div className="trading-costing-stats" style={styles.stats}>
        <div className="trading-costing-stat-card" style={styles.card}>
          <div style={styles.statLabel}>Total Costing Records</div>
          <div style={styles.statValue}>{totalCostings}</div>
        </div>

        <div className="trading-costing-stat-card" style={styles.card}>
          <div style={styles.statLabel}>Current Versions</div>
          <div style={styles.statValue}>{currentCostings}</div>
        </div>

        <div className="trading-costing-stat-card" style={styles.card}>
          <div style={styles.statLabel}>Approved Costings</div>
          <div style={styles.statValue}>{approvedCostings}</div>
        </div>
      </div>

      <div className="trading-costing-list-panel" style={styles.card}>
        <div className="trading-costing-filters" style={styles.filters}>
          <input
            type="text"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search costing number, enquiry number or remarks..."
            style={styles.input}
          />

          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            style={styles.select}
          >
            <option value="ALL">All statuses</option>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status.replaceAll("_", " ")}
              </option>
            ))}
          </select>

          <button
            type="button"
            style={{
              ...styles.button,
              background: "#fff",
              color: "#5548cb",
              border: "1px solid #dcd8ff",
            }}
            onClick={loadCostings}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="trading-costing-table-wrap" style={styles.tableWrap}>
          <table className="trading-costing-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Costing No.</th>
                <th style={styles.th}>Enquiry No.</th>
                <th style={styles.th}>Costing Date</th>
                <th style={styles.th}>Version</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Total Cost</th>
                <th style={styles.th}>Selling Amount</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} style={styles.muted}>
                    Loading trading costings...
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={styles.muted}>
                    {error
                      ? "Costings could not be loaded."
                      : "No trading costings found."}
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => (
                  <tr key={row.id}>
                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.link}
                        onClick={() => openCosting(row)}
                      >
                        {row.costing_no || `COST-${row.id}`}
                      </button>
                    </td>

                    <td
                      className="trading-costing-enquiry-no"
                      style={styles.td}
                    >
                      {getEnquiryNumber(row)}
                    </td>

                    <td style={styles.td}>
                      {formatDate(row.costing_date)}
                    </td>

                    <td style={styles.td}>
                      {row.version_no ?? 1}
                    </td>

                    <td style={styles.td}>
                      <span
                        className={`trading-costing-status status-${getStatusClass(
                          row.status_code
                        )}`}
                        style={styles.badge}
                      >
                        {normalizeStatus(row.status_code).replaceAll("_", " ")}
                      </span>
                    </td>

                    <td style={styles.td}>
                      {formatAmount(row.total_cost, row.currency_code)}
                    </td>

                    <td style={styles.td}>
                      {formatAmount(row.selling_amount, row.currency_code)}
                    </td>

                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.link}
                        onClick={() => openCosting(row)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="trading-costing-footer" style={styles.footer}>
          <span>
            Showing {filtered.length === 0 ? 0 : startIndex + 1}
            {"–"}
            {Math.min(startIndex + pageSize, filtered.length)} of{" "}
            {filtered.length} records
          </span>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              style={{
                ...styles.select,
                minWidth: "auto",
                cursor: currentPage <= 1 ? "not-allowed" : "pointer",
                opacity: currentPage <= 1 ? 0.5 : 1,
              }}
            >
              Previous
            </button>

            <span>
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() =>
                setPage((value) => Math.min(totalPages, value + 1))
              }
              style={{
                ...styles.select,
                minWidth: "auto",
                cursor:
                  currentPage >= totalPages ? "not-allowed" : "pointer",
                opacity: currentPage >= totalPages ? 0.5 : 1,
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
